import InsightLayout from "../../components/InsightLayout";

const SUBTITLE = `Die Frage „Welches LLM ist das Beste?" ist im B2B-Mittelstand falsch gestellt. Es gibt keinen Sieger, es gibt fünf Camps mit unterschiedlichen Stärken, Pricing-Strategien und Compliance-Profilen. Hier ist die Landkarte, die ich bei jedem Kunden-Onboarding zeichne — und die fünf Use-Case-Empfehlungen, die in 80 % der Mittelstands-Setups funktionieren.`;

const LlmToolLandschaft2026 = () => {
  return (
    <InsightLayout
      title="Die LLM-Tool-Landschaft 2026: Claude, ChatGPT, Gemini, DeepSeek, Llama — wer macht eigentlich was?"
      subtitle={SUBTITLE}
      metaDescription="Claude, ChatGPT, Gemini, DeepSeek, Llama: fünf Camps mit eigenen Stärken und Compliance-Profilen. Die Landkarte für die Modellwahl im Mittelstand."
      publishedDate="2026-05-26"
      readingTime="11 Min Lesezeit"
      category="AI & Tools"
    >
      <p className="text-lg">
        „Welches LLM sollten wir bei uns nutzen?" — diese Frage erreicht mich
        aktuell drei bis fünf Mal pro Woche aus Kunden-Gesprächen. Die Frage ist
        verständlich, aber sie ist falsch gestellt. Sie suggeriert, es gäbe eine
        Antwort. Tatsächlich gibt es <strong>fünf Antworten</strong>, abhängig
        davon, was Sie konkret tun wollen — und welche Compliance-Anforderungen
        Sie haben.
      </p>

      <p>
        Ich strukturiere die Landschaft 2026 in fünf Camps mit klar
        unterscheidbaren Profilen. Dieser Artikel liefert Ihnen die Landkarte,
        die Sie brauchen, um bei der nächsten Strategie-Sitzung nicht das
        falsche Modell aus dem falschen Grund auszuwählen.
      </p>

      <p>
        <strong>Nachtrag August 2026:</strong> Die Camps stehen weiterhin, das
        Gelände darunter hat sich im Sommer aber deutlich bewegt. Jeder Anbieter
        verkauft heute eine Größenstaffel statt eines Flaggschiffs, oberhalb der
        bisherigen Spitze ist eine neue Leistungsklasse entstanden, und die
        Preise sind gefallen. Die vier Verschiebungen und die Prüffragen, mit
        denen Sie künftige Releases einordnen, stehen im{" "}
        <a
          href="/insights/modell-landschaft-sommer-2026"
          className="text-[#C5A66A] underline"
        >
          Modell-Beben im Sommer 2026
        </a>
        .
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die fünf Camps
      </h2>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Camp 1: Anthropic Claude — der Agent-Spezialist
      </h3>

      <p>
        Anthropic hat sich 2024-2026 als das Camp positioniert, das am
        konsequentesten auf <strong>Agent-Workflows und Code-Tasks</strong>{" "}
        setzt. Die aktuelle Modell-Familie umfasst Opus (Flagship,
        Reasoning-Heavy), Sonnet (Workhorse, Tool-Use, Coding) und Haiku
        (Schnell, günstig, große Volumina).
      </p>

      <p>
        Was Anthropic besonders macht:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Claude Code CLI</strong> — ein Terminal-Tool, das ich selbst
          täglich nutze. Es macht Software-Engineering-Aufgaben so
          niedrigschwellig wie Schreiben.
        </li>
        <li>
          <strong>Prompt Caching</strong> mit aggressiven Discounts auf
          wiederholten Kontext. Bei längeren Konversationen oder wiederholten
          System-Prompts sparen Sie 70-90 % der Input-Kosten.
        </li>
        <li>
          <strong>Computer Use</strong> — Claude kann Bildschirm sehen, Maus +
          Tastatur steuern. Stand Mai 2026 noch im Beta, aber das Pattern wird
          Standard.
        </li>
        <li>
          <strong>EU-Hosting via AWS Bedrock</strong> in Frankfurt — wichtig
          für DSGVO-sensible Workloads.
        </li>
      </ul>

      <p>
        Wofür Claude weniger geeignet ist: Sehr breite multimodale Workflows
        (Voice, Video) — da liegt OpenAI vorn. Und für reine Mass-Inference auf
        Massen-Daten ist DeepSeek oder ein gehostetes Open-Source-Modell
        schlicht günstiger.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Camp 2: OpenAI ChatGPT/GPT-Familie — der Multimodal-Generalist
      </h3>

      <p>
        OpenAI ist nach wie vor das Camp mit der größten User-Base
        (ChatGPT-Consumer) und dem breitesten <strong>multimodalen
        Spektrum</strong>: Text, Voice (Advanced Voice Mode), Image-Generation
        (DALL-E), Video (Sora). Wenn ein Use-Case mehrere Modalitäten kombiniert
        — Sprache-zu-Text, Bild-Analyse, Audio-Generation — ist OpenAI die
        natürliche Wahl.
      </p>

      <p>
        Drei Sub-Linien laufen parallel:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>GPT-Hauptlinie</strong> — Standard-Modelle für die meisten
          Tasks
        </li>
        <li>
          <strong>o-Serie (Reasoning)</strong> — denkt vor der Antwort
          schrittweise; gut für Mathematik, Logik, komplexe Code-Probleme.
          Trade-off: deutlich langsamer + teurer
        </li>
        <li>
          <strong>GPT mini / nano</strong> — kleine, schnelle, billige Modelle
          für High-Volume-Tasks
        </li>
      </ul>

      <p>
        Wofür OpenAI weniger geeignet ist: Wer einen sehr stabilen
        Long-Term-API-Vertrag sucht, sollte wissen, dass OpenAI eine{" "}
        <em>aggressive Deprecation-Politik</em> fährt. Modelle werden in
        kürzeren Zyklen abgekündigt als bei Anthropic oder Google. Das
        bedeutet Wartungsaufwand. Mittelständler, die ihre Pipelines stabil
        halten wollen, planen das ein.
      </p>

      <p>
        EU-Hosting: über <strong>Azure OpenAI</strong> in EU-Regionen
        verfügbar — für DSGVO-Anforderungen die typische Implementierung im
        deutschen Mittelstand.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Camp 3: Google Gemini — der Kontext-König mit Workspace-DNA
      </h3>

      <p>
        Gemini hat zwei Killer-Features, die kein anderes Camp aktuell in
        gleicher Tiefe anbietet:
      </p>

      <ol className="list-decimal pl-6 space-y-2 my-4">
        <li>
          <strong>Extrem große Kontext-Fenster</strong> (mehrere Hunderttausend
          bis Millionen Token) — Sie können komplette PDF-Archive oder
          Stunden-lange Video-Files auf einmal hineingeben.
        </li>
        <li>
          <strong>Native Workspace-Integration</strong> — Gmail, Drive, Docs,
          Calendar. Wenn Ihr Unternehmen ohnehin auf Google Workspace läuft,
          ist Gemini der Pfad des geringsten Widerstands.
        </li>
      </ol>

      <p>
        Plus: Gemini hat einen großzügigen <strong>Free-Tier</strong>, was es
        zum besten Einstiegs-Modell für Pilot-Projekte macht. Hosting über{" "}
        <strong>Vertex AI in Frankfurt</strong> für DSGVO-Setups.
      </p>

      <p>
        Bonus-Hinweis aus eigener Praxis: Google bietet mittlerweile auch ein
        CLI-Tool namens <strong>Antigravity</strong> an, vergleichbar mit Claude
        Code. Ich nutze es selbst für Recherche-Sparring — es liefert eine
        zweite Modell-Perspektive zu meiner Claude-basierten Arbeit. Das ist
        die richtige Sicht auf Gemini im Pro-Setup: <em>nicht als Ersatz,
        sondern als komplementärer Worker</em>.
      </p>

      <p>
        Wofür Gemini weniger geeignet ist: Im reinen Agent-Workflow + Tool-Use
        liegt Claude noch vorn. Und die API-Verfügbarkeit der neuesten Modelle
        in EU-Regionen hinkt manchmal hinter US-Verfügbarkeit her.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Camp 4: DeepSeek — die Pricing-Disruption aus China
      </h3>

      <p>
        DeepSeek hat 2025 mit der Veröffentlichung von DeepSeek V3 + R1 die
        Frontier-Pricing-Logik gesprengt. Reasoning-Performance auf annähernd
        Claude-Opus- oder GPT-o-Niveau, bei einem Bruchteil der API-Kosten —
        teils Faktor 10 günstiger.
      </p>

      <p>
        Das verändert die Ökonomie für zwei Use-Case-Klassen radikal:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Massive Batch-Verarbeitung</strong> (Document-Processing,
          Klassifikation, Extraction)
        </li>
        <li>
          <strong>Internal-Tools</strong>, bei denen Latenz weniger kritisch
          ist als API-Kosten
        </li>
      </ul>

      <p>
        Der Haken: Der direkte API-Zugriff auf DeepSeek bedeutet Hosting in
        China. Für deutsche Mittelständler mit DSGVO-Pflichten ist das
        praktisch nie akzeptabel. Die Lösung heißt: <strong>DeepSeek-Modelle
        über europäische Inferenz-Anbieter</strong> nutzen — Together.ai, Groq
        oder Cerebras hosten die Open-Weight-Versionen mit eigenen
        DSGVO-Verträgen.
      </p>

      <p>
        Das ist der eigentliche Hebel: Open-Source-Frontier-Modelle plus
        DSGVO-konformer EU-Inferenz-Provider. Ich sehe das im Mittelstand
        2026 noch deutlich unter dem Radar — es wird in 2027 Standard sein.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        Camp 5: Open-Source-Camp — Llama, Mistral, Qwen
      </h3>

      <p>
        Meta (Llama-Familie), Mistral (französisch, EU-nah) und Alibaba (Qwen)
        liefern Open-Weight-Modelle, die Sie entweder bei Hostern wie
        Together.ai/Groq einkaufen oder <strong>selbst betreiben</strong>
        können. Die zweite Option ist die strategisch interessanteste — sie
        löst zwei Probleme gleichzeitig:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Datensouveränität</strong> — Ihre Daten verlassen Ihre VPC
          nie. Wichtig für F&E-Daten, Personalakten, Verträge.
        </li>
        <li>
          <strong>Vorhersagbare Kosten</strong> — keine API-Token-Verrechnung,
          sondern eine planbare Hardware/Hosting-Rechnung.
        </li>
      </ul>

      <p>
        Der Preis: Sie brauchen entweder eigene GPU-Infrastruktur oder einen
        Partner, der das für Sie betreibt. Realistisch bedeutet das im
        Mittelstand: ein IT-Dienstleister mit AI-Kompetenz und ein 6-stelliges
        Hardware-Investment für einen produktiven Setup. Lohnt sich ab einer
        bestimmten Inference-Volumen-Schwelle — meist im hohen 5-stelligen
        Token-Bereich pro Tag.
      </p>

      <p>
        Mistral verdient besondere Erwähnung für deutsche Mittelständler:
        französisches Unternehmen, EU-Ansässigkeit, Hosting in der EU
        verfügbar. Wer aus geopolitischen Erwägungen weg von US-Cloud will,
        findet hier den natürlichen Einstieg.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Welches Modell für welchen Use-Case? Fünf Mittelstands-Empfehlungen
      </h2>

      <p>
        Statt eine universelle Tabelle zu zeigen, hier die fünf häufigsten
        Use-Cases aus meinen Mandaten und meine konkrete Empfehlung. Diese
        Empfehlungen vermeiden bewusst hyperspezifische Modell-Versionen, da
        sich diese alle paar Monate ändern — die zugrundeliegende Logik
        bleibt.
      </p>

      <div className="overflow-x-auto my-8">
        <table className="w-full border-collapse">
          <thead className="bg-[#0F1B2D] text-white">
            <tr>
              <th className="px-4 py-3 text-left">Use-Case</th>
              <th className="px-4 py-3 text-left">Empfehlung</th>
              <th className="px-4 py-3 text-left">Warum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-3 font-medium">
                Customer-Support-Chatbot
              </td>
              <td className="px-4 py-3">
                Mid-Tier-Modell (Claude Haiku, Gemini Flash, oder gehostetes
                Llama)
              </td>
              <td className="px-4 py-3 text-sm">
                Niedrige Latenz und niedrige Stückkosten zählen, nicht
                Frontier-Reasoning. Reasoning-Modelle wie Claude Opus oder
                o-Serie wären hier ein Anti-Pattern (Latenz + Kosten ×10).
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">
                Interner Code-Assistent
              </td>
              <td className="px-4 py-3">
                Claude Sonnet via Claude Code CLI, plus Prompt Caching
              </td>
              <td className="px-4 py-3 text-sm">
                Anthropic ist im Engineering-Bereich aktuell die stabilste
                Wahl. Caching senkt Token-Kosten bei wiederholtem
                Code-Kontext um 70-90 %.
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">
                Dokument-Analyse (Verträge, Berichte)
              </td>
              <td className="px-4 py-3">
                Gemini Pro via Vertex AI Frankfurt
              </td>
              <td className="px-4 py-3 text-sm">
                Das Millionen-Token-Kontextfenster erlaubt das Einlesen
                ganzer Vertragsarchive auf einmal. Achtung: ab 200K Token
                steigen die Kosten überproportional.
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">
                Sales-Outbound-Personalisierung
              </td>
              <td className="px-4 py-3">
                Claude Sonnet oder GPT (Standard), via Batch-API
              </td>
              <td className="px-4 py-3 text-sm">
                Beide haben das beste Gespür für Tonalität und vermeiden
                den typischen „KI-Sound". Batch-API halbiert die Kosten
                bei Volumen-Outreach.
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">
                Wissens-Management / RAG
              </td>
              <td className="px-4 py-3">
                Mid-Tier-Closed-Modell oder gehostetes Open-Source —
                <em>hängt vom Datentyp ab</em>
              </td>
              <td className="px-4 py-3 text-sm">
                Bei nicht-sensiblem Wissen: Claude Sonnet / Gemini Flash
                via API. Bei sensiblen F&E- oder HR-Daten: Open-Source
                self-hosted (Llama, Mistral) auf eigener Infrastruktur.
                Pillar #9 vertieft das.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Anti-Hype-Sektion: Was die Marketing-Decks weglassen
      </h2>

      <p>
        Drei Dinge, die in keinem Anbieter-Deck stehen, aber bei der
        Setup-Entscheidung kritisch sind:
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        1. „Benchmarks" sind ein irreführender Maßstab
      </h3>
      <p>
        Wenn ein Anbieter behauptet, „90 % auf MMLU" oder „SOTA auf HumanEval"
        zu erreichen — das sagt über Ihren konkreten Use-Case fast nichts. In
        meiner Erfahrung schlägt ein gut-promptetes Mid-Tier-Modell ein
        schlecht-promptetes Frontier-Modell in 7 von 10 Fällen. Investieren
        Sie 5-10 % des AI-Budgets in interne Prompt-Engineering-Kompetenz,
        bevor Sie das nächst-teurere Modell kaufen.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        2. Pricing-Werte sind volatil
      </h3>
      <p>
        Jede Pricing-Tabelle in einem AI-Artikel ist nach drei Monaten
        veraltet. DeepSeek hat 2025 mit aggressivem Pricing den Markt
        diszipliniert, OpenAI und Google haben mehrfach im Jahr Preise
        gesenkt. Was 2026 als Faustregel gilt: <strong>Frontier-Modelle
        kosten 2-5 $ / Million Input-Token, Mid-Tier 0.5-2 $, Open-Source
        gehostet 0.1-0.5 $.</strong> Wenn Sie längerfristig planen, holen Sie
        sich beim Anbieter ein Enterprise-Angebot — das gibt Pricing-Stabilität
        für 12-24 Monate.
      </p>

      <h3 className="text-2xl font-bold text-[#0F1B2D] mt-8 mb-3">
        3. „Multi-Model-Strategy" ist Realität, kein Buzzword
      </h3>
      <p>
        Kein Mittelständler, den ich in den letzten 18 Monaten begleitet
        habe, lebt mit nur einem Provider. Die Realität ist: Claude für
        Code/Agent-Workflows, Gemini für Dokumenten-Analyse, OpenAI für
        multimodale Use-Cases, gehostetes Open-Source für sensible Daten —
        oft alles parallel. Das bedeutet auch: <strong>Ein API-Gateway oder
        Orchestrierungs-Layer wird Standard.</strong> Tools wie OpenRouter,
        Portkey oder ein eigenes Mini-Gateway (selbst gebaut in ein paar
        Tagen) entkoppeln Ihre Anwendung vom konkreten Provider.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        EU-Compliance-Schnellcheck
      </h2>

      <p>
        Für deutsche Mittelständler ist die Wahl meist nicht „technisch
        bestes Modell", sondern „technisch ausreichendes Modell mit
        akzeptablem Compliance-Profil". Hier der Schnellcheck:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>Anthropic Claude</strong> — EU-Hosting via AWS Bedrock
          Frankfurt ✓ — DPA verfügbar
        </li>
        <li>
          <strong>OpenAI</strong> — EU-Hosting via Azure OpenAI EU-Regionen ✓
          — Microsoft-DPA-Standard
        </li>
        <li>
          <strong>Google Gemini</strong> — EU-Hosting via Vertex AI Frankfurt
          ✓ — Google-DPA-Standard
        </li>
        <li>
          <strong>DeepSeek direkt</strong> — China-Hosting ✗ — nicht
          DSGVO-kompatibel ohne Umweg
        </li>
        <li>
          <strong>DeepSeek über EU-Hoster</strong> (Together.ai, Groq mit
          EU-Regionen) ✓ — DPA hängt vom Hoster ab
        </li>
        <li>
          <strong>Open-Source self-hosted</strong> — höchste
          Daten-Souveränität, aber Sie sind eigenverantwortlich für
          Compliance-Setup
        </li>
      </ul>

      <p>
        Was Sie sich in jedem Fall fragen sollten — und was die meisten
        Datenschutzbeauftragten 2026 zu Recht fordern: <em>Werden meine
        Daten zum Training des Modells genutzt?</em> Bei allen Enterprise-Tarifen
        der drei großen US-Anbieter lautet die Antwort: <strong>Nein
        (vertraglich zugesichert).</strong> Bei Consumer-Tarifen
        (ChatGPT-Plus, Gemini-Free) ist die Antwort weniger eindeutig — diese
        Tarife gehören nicht in produktive Geschäftsworkflows.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Fünf-Jahres-Outlook
      </h2>

      <p>
        Drei Entwicklungen, die ich für die nächsten 3-5 Jahre sehe und die
        Ihre Setup-Entscheidung beeinflussen sollten:
      </p>

      <p>
        <strong>Erstens: Pricing-Konvergenz.</strong> Die Differenz zwischen
        Frontier und Mid-Tier wird kleiner. Was heute „Premium" ist, wird
        2027/28 Commodity. Wenn Sie heute auf ein günstigeres Modell setzen,
        weil das teurere noch nicht im Budget liegt — die Lücke wird sich
        schließen.
      </p>

      <p>
        <strong>Zweitens: Open-Source holt auf.</strong> Llama, Mistral und
        Qwen erreichen Frontier-Niveau für 60-80 % der Use-Cases. Self-Hosting
        wird über die nächsten 24-36 Monate für deutlich mehr Mittelständler
        attraktiv — vor allem weil die Hardware-Kosten parallel sinken.
      </p>

      <p>
        <strong>Drittens: Agent-Layer wird die nächste Disruption.</strong>{" "}
        Nicht mehr „welches LLM" ist die kritische Frage, sondern „welcher
        Agent kann welchen Workflow autonom übernehmen". Das verschiebt den
        Wettbewerb von Modell-Performance zu Agent-Frameworks (MCP, Open-AI
        Assistants API, Claude Agent SDK, LangGraph). Pillar #7 und #8 dieser
        Serie gehen in die Tiefe.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die unbequeme Wahrheit
      </h2>

      <p>
        Wenn Sie diesen Artikel gelesen haben in der Hoffnung, eine einfache
        Antwort auf „Welches LLM für uns?" zu bekommen — Sie haben sie
        bekommen, aber sie ist unbequem: <strong>Es gibt nicht die eine
        richtige Antwort, und wer Ihnen das vorgaukelt, verkauft Ihnen ein
        Produkt, kein Setup.</strong>
      </p>

      <p>
        Was funktioniert: 90 Minuten Use-Case-Analyse, 5-10 Use-Cases
        identifizieren, jeweils das passende Camp wählen, einen
        Orchestrierungs-Layer dazwischen bauen. So sehen die wirklich
        funktionierenden Setups im Mittelstand 2026 aus. Multi-Camp, nicht
        Single-Vendor.
      </p>

      <p>
        Wer das nicht möchte, weil es zu komplex klingt — der ist
        wahrscheinlich noch nicht bereit für KI im Mittelstand. Dann lieber
        warten, bis sich der Markt weiter konsolidiert hat. Das wird
        passieren, aber wahrscheinlich erst 2027/28.
      </p>

      <p className="mt-12 text-sm text-gray-600 italic">
        Quellen für aktuelles Pricing und Modell-Status (Stand Mai 2026):{" "}
        <a
          href="https://www.anthropic.com/pricing"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          anthropic.com/pricing
        </a>{" "}
        ·{" "}
        <a
          href="https://openai.com/api/pricing"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          openai.com/api/pricing
        </a>{" "}
        ·{" "}
        <a
          href="https://cloud.google.com/vertex-ai/generative-ai/pricing"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          cloud.google.com/vertex-ai
        </a>{" "}
        ·{" "}
        <a
          href="https://www.deepseek.com"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          deepseek.com
        </a>{" "}
        ·{" "}
        <a
          href="https://huggingface.co/meta-llama"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          huggingface.co/meta-llama
        </a>{" "}
        ·{" "}
        <a
          href="https://mistral.ai"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          mistral.ai
        </a>
        .
      </p>
    </InsightLayout>
  );
};

export default LlmToolLandschaft2026;
